import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatsCard from '../admin-dashboard/components/StatsCard';
import MembershipOverview from './components/MembershipOverview';
import SubscriptionsList from './components/SubscriptionsList';
import RevenueMetrics from './components/RevenueMetrics';
import AlertsPanel from './components/AlertsPanel';

const MembershipManagementDashboard = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isSuperAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    churnRate: 0,
    newSubscriptions: 0,
    expiringSubscriptions: 0
  });
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isSuperAdmin))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isSuperAdmin)) {
      loadDashboardData();
    }
  }, [currentTenant, isGymOwner, isSuperAdmin, dateRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      const daysAgo = parseInt(dateRange);
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      // Load subscriptions
      const { data: subscriptionsData, error: subscriptionsError } = await supabase?.from('member_subscriptions')?.select(`
          *,
          user_profiles:member_id(id, full_name, email, avatar_url),
          membership_plans:plan_id(id, plan_name, plan_code, price)
        `)?.eq('tenant_id', currentTenant?.id)?.order('created_at', { ascending: false });

      if (subscriptionsError) throw subscriptionsError;
      setSubscriptions(subscriptionsData || []);

      // Load membership plans
      const { data: plansData, error: plansError } = await supabase?.from('membership_plans')?.select('*')?.eq('tenant_id', currentTenant?.id)?.eq('is_active', true)?.order('display_order', { ascending: true });

      if (plansError) throw plansError;
      setPlans(plansData || []);

      // Load billing records for revenue
      const { data: billingData, error: billingError } = await supabase?.from('billing_records')?.select('*')?.eq('tenant_id', currentTenant?.id)?.gte('billing_date', startDate?.toISOString()?.split('T')?.[0])?.eq('payment_status', 'completed');

      if (billingError) throw billingError;

      // Calculate stats
      const activeSubscriptions = subscriptionsData?.filter(s => s?.status === 'active')?.length || 0;
      const monthlyRevenue = billingData?.reduce((sum, b) => sum + parseFloat(b?.total_amount || 0), 0) || 0;
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);
      const newSubscriptions = subscriptionsData?.filter(
        s => new Date(s?.created_at) >= thirtyDaysAgo
      )?.length || 0;

      const sevenDaysFromNow = new Date();
      sevenDaysFromNow?.setDate(sevenDaysFromNow?.getDate() + 7);
      const expiringSubscriptions = subscriptionsData?.filter(
        s => s?.end_date && new Date(s?.end_date) <= sevenDaysFromNow && s?.status === 'active'
      )?.length || 0;

      const cancelledThisMonth = subscriptionsData?.filter(
        s => s?.cancellation_date && new Date(s?.cancellation_date) >= thirtyDaysAgo
      )?.length || 0;
      const churnRate = activeSubscriptions > 0 ? (cancelledThisMonth / activeSubscriptions) * 100 : 0;

      setStats({
        totalMembers: subscriptionsData?.length || 0,
        activeSubscriptions,
        monthlyRevenue,
        churnRate,
        newSubscriptions,
        expiringSubscriptions
      });

      // Generate alerts
      const alertsList = [];
      
      if (expiringSubscriptions > 0) {
        alertsList?.push({
          id: 'expiring',
          type: 'warning',
          title: 'Expiring Subscriptions',
          message: `${expiringSubscriptions} subscription${expiringSubscriptions > 1 ? 's' : ''} expiring in the next 7 days`,
          action: 'View Details',
          priority: 'high'
        });
      }

      const failedPayments = subscriptionsData?.filter(
        s => s?.status === 'active' && s?.next_billing_date && new Date(s?.next_billing_date) < new Date()
      )?.length || 0;
      
      if (failedPayments > 0) {
        alertsList?.push({
          id: 'failed-payments',
          type: 'error',
          title: 'Payment Issues',
          message: `${failedPayments} member${failedPayments > 1 ? 's' : ''} with overdue payments`,
          action: 'Review',
          priority: 'critical'
        });
      }

      if (churnRate > 5) {
        alertsList?.push({
          id: 'high-churn',
          type: 'warning',
          title: 'High Churn Rate',
          message: `Churn rate at ${churnRate?.toFixed(1)}% - consider retention strategies`,
          action: 'View Analytics',
          priority: 'medium'
        });
      }

      setAlerts(alertsList);

    } catch (err) {
      setError(err?.message || 'Failed to load membership data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = () => {
    navigate('/membership-plan-builder');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading membership data...</p>
          </div>
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Membership Management
            </h1>
            <p className="text-muted-foreground">
              Comprehensive oversight of memberships, subscriptions, and billing
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="px-4 py-2 bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <Button
              onClick={handleCreatePlan}
              iconName="Plus"
              iconPosition="left"
            >
              Create Plan
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" />
              <p className="text-destructive font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
          <StatsCard
            icon="Users"
            iconColor="var(--color-primary)"
            title="Total Members"
            value={stats?.totalMembers}
            subtitle="All subscriptions"
            trend=""
            trendValue=""
          />
          <StatsCard
            icon="CheckCircle"
            iconColor="var(--color-success)"
            title="Active Subscriptions"
            value={stats?.activeSubscriptions}
            subtitle="Currently active"
            trend="up"
            trendValue="+5%"
          />
          <StatsCard
            icon="DollarSign"
            iconColor="var(--color-accent)"
            title="Monthly Revenue"
            value={`${stats?.monthlyRevenue?.toFixed(2)}`}
            subtitle="Last 30 days"
            trend="up"
            trendValue="+12%"
          />
          <StatsCard
            icon="TrendingDown"
            iconColor={stats?.churnRate > 5 ? 'var(--color-destructive)' : 'var(--color-warning)'}
            title="Churn Rate"
            value={`${stats?.churnRate?.toFixed(1)}%`}
            subtitle="Last 30 days"
            trend={stats?.churnRate > 5 ? 'up' : 'down'}
            trendValue={`${stats?.churnRate?.toFixed(1)}%`}
          />
          <StatsCard
            icon="UserPlus"
            iconColor="var(--color-success)"
            title="New Subscriptions"
            value={stats?.newSubscriptions}
            subtitle="Last 30 days"
            trend="up"
            trendValue="+8"
          />
          <StatsCard
            icon="AlertTriangle"
            iconColor="var(--color-warning)"
            title="Expiring Soon"
            value={stats?.expiringSubscriptions}
            subtitle="Next 7 days"
            trend=""
            trendValue=""
          />
        </div>

        {/* Alerts Panel */}
        {alerts?.length > 0 && (
          <div className="mb-6">
            <AlertsPanel alerts={alerts} onRefresh={loadDashboardData} />
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
          <div className="xl:col-span-2">
            <MembershipOverview plans={plans} subscriptions={subscriptions} />
          </div>
          <div>
            <RevenueMetrics dateRange={dateRange} tenantId={currentTenant?.id} />
          </div>
        </div>

        {/* Subscriptions List */}
        <SubscriptionsList
          subscriptions={subscriptions}
          plans={plans}
          onRefresh={loadDashboardData}
        />
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default MembershipManagementDashboard;